 
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ImageIcon from '@mui/icons-material/Image';
import InfoIcon from '@mui/icons-material/Info';
import InventoryIcon from '@mui/icons-material/Inventory';
import TimelineIcon from '@mui/icons-material/Timeline';
import WebIcon from '@mui/icons-material/Web';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PackageDetailsBasicInformationTab from '../components/package_details/PackageDetailBasicInformationTab';
import PackageDetailsImageDialog from '../components/package_details/PackageDetailsImageDialog';
import PackageDetailsItemInfoTab from '../components/package_details/PackageDetailsItemInfoTab';
import PackageDetailsMediaTab from '../components/package_details/PackageDetailsMediaTab';
import PackageDetailsMovementTab from '../components/package_details/PackageDetailsMovementTab';
import PackageDetailsWebScrapingTab from '../components/package_details/PackageDetailsWebScrapingTab';
import fileManagerService from '../services/file-manager-service';
import packageManagerService from '../services/package-manager-service';
import { PKG } from '../utils/package-fields';

export default function PackageDetailsPage() {
  const [packageDetails, setPackageDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const openLightbox = (imgUrl) => {
    setLightboxImage(imgUrl);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage('');
  };

  useEffect(() => {
    const fetchPackageDetails = async () => {
      try {
        const response = await packageManagerService.getPackageDetails(id);
        if (response.success) {
          setPackageDetails(response.result.package);
        } else {
          console.error('Error:', response.message);
        }
      } catch (error) {
        console.error('Fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPackageDetails();
  }, [id]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  const handleDownload = async (imageUrl) => {
    try {
      await fileManagerService.downloadAndSaveFile(imageUrl);
    } catch (error) {
      console.log('Download error:', error);
      alert('File download failed.');
    }
  };

  const overviewItems = [
    { title: 'ID', value: id },
    { title: 'Company Name', value: packageDetails.company_name },
    { title: 'Direction Type', value: packageDetails.direction_type },
    { title: 'Tracking Number', value: packageDetails.tracking_number },
    {
      title: 'Batch Name',
      value: packageDetails[PKG.BATCH_NAME] || 'N/A'
    },
    {
      title: 'Order Number',
      value: packageDetails.order_number ?? 'N/A'
    },
    {
      title: 'Recipient Name',
      value: packageDetails.recipient_name || 'N/A'
    },
    {
      title: 'Uploaded By',
      value: packageDetails.user?.username || 'N/A'
    },
    {
      title: 'Date Collected',
      value: packageDetails.createdAt
        ? new Date(packageDetails.createdAt).toLocaleString()
        : 'N/A'
    },
    {
      title: 'Remarks',
      value: packageDetails.remarks || 'N/A'
    }
  ];

  const collapsibleItems = [
    {
      label: 'Scanned At Coordinates',
      items: [
        packageDetails.scanned_at_latitude,
        packageDetails.scanned_at_longitude
      ]
    },
    {
      label: 'Addresses',
      items: packageDetails.address_list
    },
    {
      label: 'Other Details',
      items: packageDetails.other_details
    },
    {
      label: 'Barcodes',
      items: packageDetails.barcode_list
    }
  ];
  return (
    <div>
      <Box
        sx={{
          p: { xs: 2, md: 4 },
          backgroundColor: '#0F1117',
          minHeight: '100vh'
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/packages')}
          sx={{ mb: 2, color: 'text.secondary' }}
        >
          Back to Packages
        </Button>

        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Package Details
        </Typography>

        {packageDetails[PKG.IS_DUPLICATE] && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This package is a duplicate — the same tracking number and company name
            were already recorded.
          </Alert>
        )}

        <Paper elevation={4} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
            variant="fullWidth"
            sx={{ backgroundColor: 'background.paper' }}
          >
            <Tab label="Overview" icon={<InfoIcon />} iconPosition="start" />
            <Tab label="Media" icon={<ImageIcon />} iconPosition="start" />
            <Tab
              label="Movement"
              icon={<TimelineIcon />}
              iconPosition="start"
            />
            <Tab label="Web Scraping" icon={<WebIcon />} iconPosition="start" />
            <Tab
              label="Item Info"
              icon={<InventoryIcon />}
              iconPosition="start"
            />
          </Tabs>

          <Box sx={{ p: 3, backgroundColor: 'background.paper' }}>
            {activeTab === 0 && (
              <PackageDetailsBasicInformationTab
                id={id}
                overviewItems={overviewItems}
                collapsibleItems={collapsibleItems}
              />
            )}

            {activeTab === 1 && (
              <PackageDetailsMediaTab
                imageList={packageDetails.image_path_list || []}
                videoList={packageDetails.video_path_list || []}
                shippingLabelPhotos={packageDetails.shipping_label_photos || []}
                packagePhotos={packageDetails.package_photos || []}
                items={packageDetails.items || []}
                onImageClick={openLightbox}
              />
            )}

            {activeTab === 2 && (
              <PackageDetailsMovementTab
                movementHistory={
                  packageDetails.zoeShipmentTrackingLog
                    ? [packageDetails.zoeShipmentTrackingLog]
                    : []
                }
              />
            )}

            {activeTab === 3 && (
              <PackageDetailsWebScrapingTab
                webScrapingHistory={packageDetails.trackingRecord || {}}
              />
            )}

            {activeTab === 4 && (
              <PackageDetailsItemInfoTab
                items={packageDetails.items || []}
                onImageClick={openLightbox}
              />
            )}
          </Box>
        </Paper>

        {/* Lightbox Dialog */}
        <PackageDetailsImageDialog
          open={lightboxOpen}
          onClose={closeLightbox}
          imageUrl={lightboxImage}
          onDownload={() => handleDownload(lightboxImage)}
        />
      </Box>
    </div>
  );
}
